import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AdminDashboardWrapper,
  PortfolioTable,
  adminUserConfig,
} from "../../components/portfolio";
import type { PortfolioRow } from "../../components/portfolio/columns/columnTypes";
import type { PortfolioTableConfig } from "../../components/portfolio/configs/types";
import PositionsTabToggle, { type PositionsTab } from "../../components/portfolio/PositionsTabToggle";
import { useAdminManagedStrategies } from "../../hooks/useAdminManagedStrategies";
import { usePortfolioData } from "../../hooks/usePortfolioData";
import { useStrategyData } from "../../hooks/useStrategyData";
import { useStrategyHoldings } from "../../hooks/useStrategyHoldings";
import { holdingsToPortfolioRows } from "../../utils/holdingsToPortfolioRows";
import { rollUpInvestorStrategyProducts } from "../../utils/managedStrategyRollup";

const adminTargetConfig: PortfolioTableConfig = {
  id: "admin-target-holdings",
  columns: [
    "symbol",
    "targetWeightPct",
    "mktValueUsd",
    "currency",
    "secType",
    "group",
  ],
  expandedColumns: ["conId"],
  features: {
    search: true,
    filters: true,
    grouping: false,
    export: true,
  },
  dataRequirements: {
    positions: false,
    accountSummary: true,
    batchPrices: false,
    targetPortfolio: false,
    includeSynthetics: false,
    tradableTimes: false,
  },
};

const adminCombinedConfig: PortfolioTableConfig = {
  id: "admin-combined-holdings",
  columns: [
    "symbol",
    "targetWeightPct",
    "currentWeightPct",
    "weightDeviation",
    "unrealizedPnlUsd",
    "mktValueUsd",
    "qty",
    "currency",
    "secType",
  ],
  expandedColumns: [
    "mktValueLocal",
    "costBasisLocal",
    "mktPrice",
    "priceUsd",
    "sector",
    "group",
    "conId",
  ],
  features: {
    search: true,
    filters: true,
    grouping: true,
    export: true,
  },
  dataRequirements: {
    positions: false,
    accountSummary: true,
    batchPrices: false,
    targetPortfolio: false,
    includeSynthetics: false,
    tradableTimes: false,
  },
};

const AdminUserDashboardPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const data = usePortfolioData({ config: adminUserConfig, userId });
  const [activeTab, setActiveTab] = useState<PositionsTab>("combined");

  const {
    strategies,
    reservedEntries,
    productItems,
    basketLookup,
    isLoading: isLoadingStrategy,
    strategyError,
    refetchStrategies,
    refetchProducts,
  } = useStrategyData(userId);

  // Refresh has to reach the managed compositions too, or the Target tab keeps
  // rendering the composition that was current when the page mounted.
  const [managedReloadToken, setManagedReloadToken] = useState(0);

  // A managed strategy is a held product like any other, so the model is built
  // from every record. reservedEntries carries the MANAGED_STRATEGY rows that
  // the self-directed `strategies` view filters out.
  const records = useMemo(
    () => [...strategies, ...reservedEntries],
    [strategies, reservedEntries]
  );

  const managedIds = useMemo(
    () =>
      records
        .filter((record) => record.entityType === "MANAGED_STRATEGY")
        .map((record) => record.productId),
    [records]
  );
  const managed = useAdminManagedStrategies(managedIds, true, managedReloadToken);

  // Flatten to portfolio/basket level so a managed strategy's leaves reach the
  // holdings math. Direct holdings pass through unchanged.
  const rollup = useMemo(
    () =>
      rollUpInvestorStrategyProducts(
        records,
        managed.managedById,
        productItems,
        basketLookup
      ),
    [records, managed.managedById, productItems, basketLookup]
  );

  // A short model on a live book reads as "the user targets less than they do",
  // so an incomplete expansion must always be visible.
  const rollupWarning = useMemo(() => {
    if (managed.isLoading) return null;
    // Count distinct strategies, not issue rows: a failed fetch also surfaces
    // as MANAGED_NOT_FOUND from the rollup, and must not be counted twice.
    const unresolvedManaged = managedIds.filter((id) => !managed.managedById.has(id)).length;
    if (unresolvedManaged > 0) {
      return `Target is incomplete: ${unresolvedManaged} managed strateg${
        unresolvedManaged === 1 ? "y" : "ies"
      } could not be loaded.`;
    }
    if (rollup.issues.some((issue) => issue.code === "MANAGED_COMPOSITION_INVALID")) {
      return "Target is incomplete: a managed strategy composition is unavailable.";
    }
    if (rollup.issues.some((issue) => issue.code === "PRODUCT_NOT_FOUND")) {
      return "Target is incomplete: a product could not be resolved from the catalogue.";
    }
    return null;
  }, [managed.isLoading, managed.managedById, managedIds, rollup.issues]);

  const holdingsInput = useMemo(
    () =>
      rollup.rows.map((row) => ({
        entityType: row.entityType,
        productId: row.productId,
        weight: row.allocatedWeight,
        driftedWeight: row.currentWeight,
        displayName: row.displayName,
      })),
    [rollup.rows]
  );
  const holdings = useStrategyHoldings(holdingsInput, productItems, basketLookup);

  const targetRows = useMemo(
    () =>
      holdingsToPortfolioRows(
        holdings.holdings,
        data.accountSummary?.netLiquidationSecurity ?? null,
        // currentWeightPct means live IBKR everywhere else on this page; a
        // modeled row must not occupy it.
        { modelOnly: true }
      ),
    [holdings.holdings, data.accountSummary]
  );

  const actualRowsWithTarget = useMemo(() => {
    if (!targetRows.length) return data.rows;
    const targetByConId = new Map(targetRows.map((r) => [r.conId, r.targetWeightPct]));
    return data.rows.map((row) => {
      const tgt = targetByConId.get(row.conId);
      return tgt !== undefined ? { ...row, targetWeightPct: tgt } : row;
    });
  }, [data.rows, targetRows]);

  const combinedRows = useMemo(() => {
    const merged: PortfolioRow[] = [];
    const seen = new Set<number>();

    for (const actual of data.rows) {
      seen.add(actual.conId);
      const target = targetRows.find((t) => t.conId === actual.conId);
      merged.push({ ...actual, targetWeightPct: target?.targetWeightPct ?? null });
    }

    for (const target of targetRows) {
      if (seen.has(target.conId)) continue;
      merged.push({
        ...target,
        currentWeightPct: null,
        unrealizedPnlUsd: null,
        mktValueUsd: null,
        shares: 0,
        mktValue: 0,
        mktPrice: 0,
        avgCost: 0,
      });
    }

    return merged;
  }, [data.rows, targetRows]);

  const handleRefresh = useCallback(() => {
    refetchStrategies();
    refetchProducts();
    setManagedReloadToken((token) => token + 1);
    holdings.retry();
    return data.refresh();
  }, [refetchStrategies, refetchProducts, holdings, data]);

  if (!userId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-500">User ID is required</p>
      </div>
    );
  }

  const configForTab =
    activeTab === "target"
      ? adminTargetConfig
      : activeTab === "combined"
        ? adminCombinedConfig
        : adminUserConfig;

  const rowsForTab =
    activeTab === "target"
      ? targetRows
      : activeTab === "combined"
        ? combinedRows
        : actualRowsWithTarget;

  const isLoadingTab =
    activeTab === "actual"
      ? data.isLoading
      : holdings.isLoading || isLoadingStrategy || managed.isLoading || data.isLoading;

  return (
    <AdminDashboardWrapper
      userId={userId}
      accountCode={data.accountSummary?.accountCode}
    >
      <div className="h-full flex flex-col">
        {activeTab !== "actual" && rollupWarning && (
          <div className="flex-shrink-0 mx-3 mt-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-300">
            {rollupWarning}
          </div>
        )}
        <div className="flex-1 overflow-hidden">
          <PortfolioTable
            config={configForTab}
            rows={rowsForTab}
            accountSummary={data.accountSummary}
            isLoading={isLoadingTab}
            isPartialData={activeTab === "actual" ? data.isPartialData : false}
            error={(() => {
              const stratErr = strategyError ? new Error(strategyError) : null;
              const summaryErr =
                !data.isLoading && !data.accountSummary
                  ? new Error("Account summary unavailable")
                  : null;
              if (activeTab === "actual") return data.error;
              if (activeTab === "target") return stratErr || holdings.error || summaryErr;
              return data.error || stratErr || holdings.error;
            })()}
            lastUpdatedAt={data.lastUpdatedAt}
            onRefresh={handleRefresh}
            belowControlsSlot={
              <PositionsTabToggle activeTab={activeTab} onTabChange={setActiveTab} />
            }
          />
        </div>
      </div>
    </AdminDashboardWrapper>
  );
};

export default AdminUserDashboardPage;
